import * as React from "react"
import {
  Card,
  CardSection,
  ThemeCss,
  Link,
  useShowSuccessToast,
} from "gatsby-interface"

import {
  ConnectedDomains,
  GatsbyCloudCardHeader,
  GatsbyCloudCdnFeatures,
  SslCertificates,
} from "@modules/cdn/gatsbyCloudCdn"
import {
  gatsbyCloudCdnIntegrationConfig as text,
  errorAlert as errorText,
} from "@modules/locales/default.json"
import { CdnVendor } from "@modules/graphql/types"
import { useHostingDetailsForSiteQuery } from "@modules/cdn/gatsbyCloudCdn/queries.generated"
import { useSupportedCdnIntegrationsQuery } from "@modules/cdn/shared/queries.generated"
import { useTriggerErrorAlert } from "@modules/ui/components/ErrorAlert"
import { FormattedMessage } from "@modules/locales"
import { useConnectCloudCdn } from "./GatsbyCloudCdn.helpers"
import Loading from "@modules/ui/components/Loading"
import { useTracker, SegmentEventType } from "@modules/analytics"

enum ConnectState {
  Connected = `CONNECTED`,
  NotConnected = `NOT_CONNECTED`,
  Connecting = `CONNECTING`,
}

const descriptionCss: ThemeCss = theme => ({
  fontSize: theme.fontSizes[1],
  marginTop: `-${theme.space[6]}`,
  color: theme.colors.grey[50],
})

export type GatsbyCloudCdnProps = {
  siteId: string
  siteName: string
}

export function GatsbyCloudCdn({ siteId, siteName }: GatsbyCloudCdnProps) {
  const { trackSegment } = useTracker()
  const { connectCloudCdn, disconnectCloudCdn } = useConnectCloudCdn(siteId)
  const [setError, errorAlert] = useTriggerErrorAlert()
  const showSuccessToast = useShowSuccessToast()

  const {
    data: cdnIntegrationsData,
    loading: cdnIntegrationLoading,
  } = useSupportedCdnIntegrationsQuery({
    variables: { siteId },
    onError: setError,
  })

  const cdnIntegrations = cdnIntegrationsData?.supportedCDNIntegrations ?? []

  const isConnectedToGatsbyCdn = cdnIntegrations.some(
    ({ vendor, connected }) =>
      vendor === CdnVendor.CloudCdn && connected === true
  )

  const {
    data: hostingDetails,
    loading: hostingDetailsLoading,
  } = useHostingDetailsForSiteQuery({
    variables: { siteId },
    pollInterval: 15000,
    onError: setError,
    skip: !isConnectedToGatsbyCdn,
  })

  const hostingDetailsForSite = hostingDetails?.hostingDetailsForSite?.[0]

  const atLeastOneCustomDomainConnected = hostingDetailsForSite?.domains?.some(
    item => !item.cloudCdnDefault
  )

  const [connectState, setConnectState] = React.useState<ConnectState>(
    isConnectedToGatsbyCdn ? ConnectState.Connected : ConnectState.NotConnected
  )

  React.useEffect(() => {
    setConnectState(
      isConnectedToGatsbyCdn
        ? ConnectState.Connected
        : ConnectState.NotConnected
    )
  }, [cdnIntegrationsData])

  const enableCloudCdn = () => {
    setConnectState(ConnectState.Connecting)

    connectCloudCdn()
      .then(result => {
        if (result?.success) {
          setConnectState(ConnectState.Connected)
          return showSuccessToast(text.messages.gatsbyCloudTurnedOn)
        }

        setConnectState(ConnectState.NotConnected)

        const errMessage =
          result?.message || errorText.messages.somethingWentWrong
        return setError({ message: errMessage })
      })
      .catch(err => {
        setConnectState(ConnectState.NotConnected)
        setError({ message: err.message })
      })

    trackSegment({
      type: SegmentEventType.Track,
      event: "Gatsby Hosting enabled",
      properties: {
        location: "Site Settings",
      },
    })
  }

  const disableCloudCdn = () => {
    disconnectCloudCdn()
      .then(result => {
        if (result?.success) {
          setConnectState(ConnectState.NotConnected)
          return showSuccessToast(text.messages.gatsbyCloudTurnedOff)
        }

        const errMessage =
          result?.message || errorText.messages.somethingWentWrong
        return setError({ message: errMessage })
      })
      .catch(err => setError({ message: err.message }))

    trackSegment({
      type: SegmentEventType.Track,
      event: "Gatsby Hosting disabled",
      properties: {
        location: "Site Settings",
      },
    })
  }

  const isConnected = connectState === ConnectState.Connected

  return (
    <Card>
      <GatsbyCloudCardHeader
        siteName={siteName}
        isConnected={isConnected}
        connectState={connectState}
        disableCloudCdn={disableCloudCdn}
        enableCloudCdn={enableCloudCdn}
      />

      <CardSection applyPaddingTop={false}>
        {isConnected ? (
          <p css={descriptionCss}>
            <FormattedMessage<never, "link">
              message={text.messages.success}
              tags={{
                // @TODO we need a more direct link to the doc when it's ready
                link: function(content) {
                  return (
                    <Link href="https://support.gatsbyjs.com/">{content}</Link>
                  )
                },
              }}
            />
          </p>
        ) : (
          <GatsbyCloudCdnFeatures css={descriptionCss} />
        )}

        {isConnected && (cdnIntegrationLoading || hostingDetailsLoading) && (
          <Loading
            bufferSize="none"
            delay={1000}
            message={text.messages.loadingDetails}
          />
        )}

        {errorAlert}

        {isConnected && hostingDetailsForSite?.domains && (
          <ConnectedDomains
            siteId={siteId}
            hostingDetails={hostingDetailsForSite}
            css={theme => ({
              marginTop: theme.space[6],
            })}
          />
        )}

        {isConnected &&
          atLeastOneCustomDomainConnected &&
          hostingDetailsForSite?.certificates && (
            <SslCertificates
              siteId={siteId}
              hostingDetails={hostingDetailsForSite}
            />
          )}
      </CardSection>
    </Card>
  )
}
